import axiosInstance from './axiosInstance';

export type Asset = {
  assetId: string;
  assetNameId: string;
  assetEquipmentId: string;
  name: string;
  type: string;
  subType: string;
  orgId: string;
  orgName: string;
  locationId: string;
  location: string;
  departmentId: string;
  departmentName: string;
  vendorId: string;
  vendorName: string;
  installedDate: string;
  status: string;
  actions: string;
  calibrationId: string;
  certificateCount: number;
  leased?: {
    contractorId: string;
    contractorName: string;
    leaseStartDate: string;
    leaseEndDate: string;
    isLeased?: boolean;
  };
  certificateInfo?: {
    certificateId: string;
    noOfCertificates: number;
    totalCertificates: number;
  };
};

export type Calibration = {
  calibrationId: string;
  assetEquipmentId: string;
  assetId: string;
  assetName: string;
  calibrationDate: string;
  calibrationDueDate: string;
  calibrationFrequency: string;
  calibrationName: string;
  certificateId: string;
  contactEmail: string;
  contactName: string;
  contactNumber: string;
  createdAt: string;
  daysToExpire: number;
  departmentId: string;
  departmentName: string;
  installedDate: string;
  locationId: string;
  locationName: string;
  modifiedAt: string;
  notes: string;
  organizationName: string;
  orgId: string;
  priority: string;
  status: string;
  vendorId: string;
  vendorName: string;
};

export type AssetWithCalibration = {
  asset: Asset;
  calibration: Calibration;
};

export type Organization = {
  orgId: string;
  orgName: string;
};

export type Department = {
  departmentId: string;
  departmentName: string;
};

export type Location = {
  locationId: string;
  location: string;
  departments?: Department[];
};


export type Vendor = {
  vendorId: string;
  name: string;
};

export type HeaderContext = {
  orgInfo?: {
    id: string;
    name: string;
    contact: string;
    gsTin: string;
  } | null;
  locationInfo?: {
    id: string;
    name: string;
    city: string;
    state: string;
    zipcode?: string;
  } | null;
  assetInfo?: {
    id: string | null;
    name: string;
    type: string;
    status: string;
  } | null;
  vendorInfo?: {
    id: string;
    name: string;
    contactNumber: string;
    gsTin: string;
  } | null;

};

interface payload {
  id: string;
  assetId: string;
  name: string;
  typeId: string;
  orgId: string;
  orgName: string;
  location: string;
  vendorId: string;
  vendorName: string;
  installedDate: string;
  status: string;
}

export type Contractor = {
  contractorId: string;
  name: string;
};

export const assetRepository = {

  getAssets: async (): Promise<Asset[]> => {
    const { data } = await axiosInstance.get<Asset[]>('/api/assets');

    const currentUserOrgId = localStorage.getItem("orgId");

    return (Array.isArray(data) ? data : []).map((a: any) => ({
      ...a,
      isLeased: a.orgId !== currentUserOrgId,
    }));
  },

  // Add new asset 
  addAsset: async (form: Omit<Asset, 'actions'>): Promise<Asset> => {
    const payload: any = {
      assetEquipmentId: form.assetEquipmentId,
      assetNameId: form.assetNameId,
      name: form.name,
      type: form.type,
      subType: form.subType,
      orgId: form.orgId,
      orgName: form.orgName,
      locationId: form.locationId,
      location: form.location,
      departmentId: form.departmentId,
      departmentName: form.departmentName,
      vendorId: form.vendorId,
      vendorName: form.vendorName,
      installedDate: form.installedDate ? new Date(form.installedDate).toISOString() : undefined,
      status: form.status,
    };
    Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

    const { data } = await axiosInstance.post('/api/assets', payload);
    return data;
  },


  // Delete asset
  deleteAsset: async (assetId: string) => {
    return await axiosInstance.delete(`/api/assets/${assetId}`);
  },

  // Get organizations 
  getOrganizations: async (): Promise<Organization[]> => {
    const response = await axiosInstance.get('/api/organizations');

    // Handle different response structures
    const rawData =
      response.data?.data ||
      response.data?.organizations ||
      response.data;

    const arr = Array.isArray(rawData) ? rawData : [rawData];

    const mapped = arr
      .map((o: any) => ({
        orgId:
          o.orgId ||
          o.organizationId ||
          o.org_id ||
          o.id ||
          o._id ||
          "",

        orgName:
          o.orgName ||
          o.organizationName ||
          o.org_name ||
          o.name ||
          "",
      }))
      .filter((o) => o.orgId && o.orgName);
    return mapped;
  },

  getLocations: async (orgId: string): Promise<Location[]> => {
    const { data } = await axiosInstance.get(`/api/organizations/org id,loc id,loc name,dept id,dept name`);

    const organizationData = Array.isArray(data) ? data.find(o => o.orgId === orgId) : data;

    if (!organizationData || !Array.isArray(organizationData.locations)) {
      return [];
    }

    const normalized: Location[] = organizationData.locations
      .map((l: any) => ({
        locationId: l.locId || l.locationId || l.id || l._id || '',
        location: l.locName || l.name || l.locationName || '',
        departments: (l.departments || [])
          .map((d: any) => ({
            departmentId: d.departmentId || d.id || d._id || '',
            departmentName: d.departmentName || d.name || '',
          }))
          .filter((d: Department) => d.departmentId),
      }))
      .filter((l: Location) => l.locationId && l.location);

    return normalized;
  },
  // Get vendors
  getVendors: async (): Promise<Vendor[]> => {
    const response = await axiosInstance.get('/api/vendors');

    const vendors = response.data?.data || [];

    return vendors.map((v: any) => ({
      vendorId: v.vendorId,
      name: v.name,
    }));
  },
  // Update asset 

  updateAsset: async (assetId: string, form: Partial<Asset>): Promise<Asset> => {
    const { data: existing } = await axiosInstance.get(`/api/assets/${assetId}`);
    const payload = { ...existing, ...form };
    const { data } = await axiosInstance.put(`/api/assets/${assetId}`, payload);

    return data;
  },

  // Asset list export with filters
  exportOrgAsset: async (
    format: 'csv' | 'xlsx' = 'csv',
    filters?: {
      search?: string;
      type?: string;
      status?: string;
      vendorName?: string;
      location?: string;
      departmentName?: string;
      orgName?: string;
      isLeased?: boolean | null;
    }
  ): Promise<Blob> => {
    const response = await axiosInstance.get('/api/assets/export', {
      params: {
        format,
        search: filters?.search,
        type: filters?.type,
        status: filters?.status,
        vendorName: filters?.vendorName,
        location: filters?.location,
        departmentName: filters?.departmentName,
        orgName: filters?.orgName,
        isLeased: filters?.isLeased,
      },
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    });

    return response.data;
  },

  getAssetIdsAndNames: async (): Promise<{ assetId: string; name: string }[]> => {
    const { data } = await axiosInstance.get("/api/assets/Id's-Names");
    return data.map((a: any) => ({
      assetId: a.assetId,
      name: a.name,
    }));
  },

  // Get vendor products by vendorId
  getVendorProducts: async (vendorId: string): Promise<any[]> => {
    const { data } = await axiosInstance.get(`/api/VendorProducts/vendor/${vendorId}`);
    const products = data?.[0]?.products ?? [];
    return products.map((p: any) => ({
      productId: String(p.productId),
      productName: p.productName,
      productType: p.productType,
      productDescription: p.productDescription,
      productStatus: p.productStatus,
    }));
  },

  // Get asset types
  getProductTypes: async (): Promise<{ id: string; type: string; subType: string[] }[]> => {
    const { data } = await axiosInstance.get(`/api/ProductTypes`);
    return Array.isArray(data) ? data.map((t: any) => ({
      id: t.id,
      type: t.type,
      subType: t.subType || [],
    })) : [];
  },


  getHeaderContext: async (
    orgId: string,
    locId: string,
    assetId: string,
    vendorId: string,
  ): Promise<HeaderContext> => {
    const { data } = await axiosInstance.get<HeaderContext>(
      '/api/calibrations/headercontext',
      {
        params: {
          orgid: orgId || '',
          locid: locId || '',
          assetid: assetId || '',
          vendorid: vendorId || '',
        },
      }
    );
    return data;
  },


  // Get paginated assets with filters
  getPaginatedAssets(
    pageNumber: number,
    pageSize: number,
    search?: string,
    type?: string,
    status?: string,
    vendorName?: string,
    location?: string,
    departmentName?: string,
    orgName?: string,
    appliedSearch?: string,
    isLeased?: boolean | null
  ) {
    return axiosInstance.get(`/api/assets`, {
      params: {
        pageNumber,
        pageSize,
        search,
        type,
        status,
        vendorName,
        location,
        departmentName,
        orgName,
        appliedSearch,
        isLeased
      }
    }).then(res => res.data);
  },

  // Get assets with calibration details
  getAssetsWithCalibration: async (): Promise<AssetWithCalibration[]> => {
    const { data } = await axiosInstance.get('/api/assets/with-calibration');

    return (Array.isArray(data) ? data : []).map((item: any) => ({
      asset: {
        assetId: item.asset.assetId,
        assetNameId: item.asset.assetNameId,
        assetEquipmentId: item.asset.assetEquipmentId,
        name: item.asset.name,
        type: item.asset.type,
        subType: item.asset.subType,
        orgId: item.asset.orgId,
        orgName: item.asset.orgName,
        locationId: item.asset.locationId,
        location: item.asset.location,
        departmentId: item.asset.departmentId,
        departmentName: item.asset.departmentName,
        vendorId: item.asset.vendorId,
        vendorName: item.asset.vendorName,
        installedDate: item.asset.installedDate,
        status: item.asset.status,
        calibrationId: item.asset.calibrationId,
        certificateCount: item.asset.certificateCount,
        actions: '',
      },
      calibration: {
        ...item.calibration,
      },
    }));
  },
  getContractors: async (): Promise<Contractor[]> => {
    const { data } = await axiosInstance.get('/api/Contractor/contractor-id-name');

    return (Array.isArray(data) ? data : []).map((c: any) => ({
      contractorId: c.id,
      name: c.name,
    }));
  },
  leaseAssetToContractor: async (data: {
    assetId: string;
    contractorId?: string;
    contractorName?: string;
    leaseStartDate?: string;
    leaseEndDate?: string;
  }) => {
    return await axiosInstance.patch(
      `/api/assets/${data.assetId}/lease-contractor`,
      {
        contractorId: data.contractorId,
        contractorName: data.contractorName,
        leaseStartDate: data.leaseStartDate
          ? new Date(data.leaseStartDate).toISOString()
          : null,

        leaseEndDate: data.leaseEndDate
          ? new Date(data.leaseEndDate).toISOString()
          : null,
      }
    );
  },

  unleaseAssetFromContractor: async (assetId: string) => {
    const { data } = await axiosInstance.patch(
      `/api/assets/${assetId}/unlease-contractor`
    );
    return data;
  },

  UpdateLease: async (
    assetId: string,
    data: {
      contractorId?: string;
      contractorName?: string;
      leaseStartDate?: string;
      leaseEndDate?: string;
    }
  ) => {
    return await axiosInstance.patch(
      `/api/assets/${assetId}/renew-lease`,
      {
        contractorId: data.contractorId,
        contractorName: data.contractorName,

        leaseStartDate: data.leaseStartDate
          ? new Date(data.leaseStartDate).toISOString()
          : null,

        leaseEndDate: data.leaseEndDate
          ? new Date(data.leaseEndDate).toISOString()
          : null,
      }
    );
  },

  getLeasedOrganizations: async (): Promise<Organization[]> => {
    const { data } = await axiosInstance.get(`/api/organizations/leased-orgs`);

    return (Array.isArray(data) ? data : []).map((o: any) => ({
      orgId: o.orgId || o.id,
      orgName: o.orgName || o.name,
    }));
  },
};
